/**
 * Map search box — spec §F5.10: "Search box (top, next to sidebar toggle):
 * `/hierarchy/search` debounced 200 ms ≥ 2 chars; grouped rows name + `level · country`;
 * select → fitBounds + open unit profile."
 *
 * Markup and geometry are the mockup's `#mapsearch` / `#searchResults` (290 px at
 * `top:12px; left:56px`, i.e. immediately right of `#collapseSb`). Three things the mockup
 * did not have, because it searched a local object:
 *
 * * the debounce is real (200 ms) and the query is keyed `['search', q, country]`, so
 *   re-typing a term already searched costs nothing;
 * * **keyboard navigation** — the mockup was mouse-only, and a search box that cannot be
 *   driven from the keyboard fails §I5's accessibility line. Rows are an ARIA listbox,
 *   ↑/↓ move `aria-activedescendant`, Enter selects, Esc closes;
 * * the fit comes from `/hierarchy/units/{id}.bbox` (added for this panel) rather than from
 *   a polygon the client would otherwise download whole. The already-cached choropleth
 *   FeatureCollection is the fallback when a unit has no geometry-derived bbox.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import L from 'leaflet';

import {
  fetchUnitDetail,
  queryKeys,
  searchUnits,
  type SearchHit,
  type UnitsFC,
} from '../../lib/api';
import { selectScopeParent, useAppStore } from '../../lib/state';
import { useMap } from './mapContext';
import { fitTo } from './panes';

/** §F5.10 "debounced 200 ms ≥ 2 chars". */
export const SEARCH_DEBOUNCE_MS = 200;
export const SEARCH_MIN_CHARS = 2;

type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

export function SearchBox() {
  const { t } = useTranslation();
  const map = useMap();
  const queryClient = useQueryClient();
  const country = useAppStore((s) => s.country);
  // §F5.8: the box searches inside the filter. A scoped map that still offers to fly the
  // reader to a commune it is no longer painting is the one control contradicting the rest.
  const parent = useAppStore(selectScopeParent);
  const setSelectedUnit = useAppStore((s) => s.setSelectedUnit);

  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setTerm(text.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [text]);

  const enabled = term.length >= SEARCH_MIN_CHARS;
  const { data } = useQuery({
    queryKey: queryKeys.search(term, country, parent),
    queryFn: () => searchUnits(term, country, parent),
    enabled,
  });
  const hits = useMemo<SearchHit[]>(() => (enabled ? (data ?? []) : []), [enabled, data]);

  // A new result set must not leave the highlight pointing past its end.
  useEffect(() => setActive(0), [hits]);

  // The mockup's `document.click` guard: anything outside `#mapsearch` closes the list.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  /** Fit the map to the unit, preferring the API's bbox over any client-side geometry.
   *
   * The move itself is `panes.fitTo`, which stops any animation already in flight — a
   * second search result picked while the map is still easing to the first one would
   * otherwise be discarded silently (NOTES #312). */
  const focusUnit = async (id: string): Promise<void> => {
    const detail = await queryClient.fetchQuery({
      queryKey: queryKeys.unit(id),
      queryFn: () => fetchUnitDetail(id),
    });
    if (detail.bbox) {
      const [minx, miny, maxx, maxy] = detail.bbox;
      fitTo(map, [
        [miny, minx],
        [maxy, maxx],
      ]);
      return;
    }
    // Fallback: a FeatureCollection already in the cache (the choropleth's or a hierarchy
    // row's) may carry the geometry, and measuring it costs no request.
    for (const [, fc] of queryClient.getQueriesData<UnitsFC>({ queryKey: ['units'] })) {
      const feature = fc?.features.find((f) => f.properties.id === id);
      if (!feature) continue;
      const bounds = L.geoJSON(feature as unknown as GeoJsonInput).getBounds();
      fitTo(map, bounds);
      return;
    }
  };

  const select = (hit: SearchHit): void => {
    setOpen(false);
    setText('');
    setTerm('');
    // Open the §F5.7 profile first: it is the answer to the search, and it must not wait
    // on the fit (which is a round trip when the unit is not yet in the cache).
    setSelectedUnit(hit.id);
    void focusUnit(hit.id);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open || hits.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % hits.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + hits.length) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[active];
      if (hit) select(hit);
    }
  };

  const listOpen = open && enabled;

  return (
    <div id="mapsearch" ref={boxRef}>
      <input
        id="searchInput"
        ref={inputRef}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-expanded={listOpen}
        aria-haspopup="listbox"
        aria-controls="searchResults"
        aria-autocomplete="list"
        aria-activedescendant={listOpen && hits[active] ? `sr-${hits[active].id}` : undefined}
        aria-label={t('search.label')}
        placeholder={t('search.placeholder')}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {text ? (
        <button
          type="button"
          id="searchClear"
          aria-label={t('search.clear')}
          title={t('search.clear')}
          onClick={() => {
            setText('');
            setTerm('');
            setOpen(false);
            inputRef.current?.focus();
          }}
        >
          ×
        </button>
      ) : null}

      {listOpen ? (
        <div id="searchResults" role="listbox" aria-label={t('search.results')}>
          {hits.length === 0 ? (
            <div className="sr-empty">
              <small>{t('search.empty')}</small>
            </div>
          ) : (
            hits.map((hit, i) => (
              <div
                key={hit.id}
                id={`sr-${hit.id}`}
                role="option"
                aria-selected={i === active}
                className={i === active ? 'active' : undefined}
                data-hit={hit.id}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault() /* keep focus for Esc/↑↓ */}
                onClick={() => select(hit)}
              >
                <span>{hit.name}</span>
                <small>
                  {hit.level_label}
                  {hit.country_name ? ` · ${hit.country_name}` : ''}
                </small>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export default SearchBox;
